"use client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/Dialog";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Label } from "../ui/Label";
import { Card, CardContent } from "../ui/Card";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { customerService } from "../../services/customers";
import { toast } from "sonner";

export interface Custo {
  id?: number;
  description: string;
  cost: number;
  user_id?: number;
  product_id?: number;
  created_at?: string;
}

interface AdicionarManutencaoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  produtoNome?: string;
  produtoId?: number;
  clienteId?: number | string;
  custosExistentes?: Custo[];
}

export function AdicionarManutencaoDialog({
  open,
  onOpenChange,
  onSuccess,
  produtoNome,
  produtoId,
  clienteId,
  custosExistentes = []
}: AdicionarManutencaoDialogProps) {
  const [custos, setCustos] = useState<Custo[]>([]);
  const [novoCusto, setNovoCusto] = useState<Custo>({
    cost: 0,
    description: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setCustos(custosExistentes);
      setNovoCusto({ cost: 0, description: "" });
    }
  }, [open, custosExistentes]);

  const adicionarCusto = async () => {
    if (novoCusto.description && novoCusto.cost > 0 && produtoId && clienteId) {
      setIsSubmitting(true);
      try {
        const response = await customerService.addMaintenanceCost(clienteId, produtoId, novoCusto);
        if (response.data.success) {
          setCustos([...custos, response.data.data]);
          setNovoCusto({ cost: 0, description: "" });
          toast.success("Custo de manutenção adicionado com sucesso!");
          onSuccess?.();
        }
      } catch (error) {
        console.error("Erro ao adicionar custo:", error);
        toast.error("Erro ao adicionar custo de manutenção.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const removerCusto = async (custoId: number | undefined, index: number) => {
    if (!custoId || !produtoId || !clienteId) {
      // Se não tem ID, é um custo local (não deve acontecer com a API, mas por segurança)
      setCustos(custos.filter((_, i) => i !== index));
      return;
    }

    setIsDeleting(custoId);
    try {
      const response = await customerService.deleteMaintenanceCost(clienteId, produtoId, custoId);
      if (response.data.success) {
        setCustos(custos.filter(c => c.id !== custoId));
        toast.success("Custo removido com sucesso!");
        onSuccess?.();
      }
    } catch (error) {
      console.error("Erro ao remover custo:", error);
      toast.error("Erro ao remover custo de manutenção.");
    } finally {
      setIsDeleting(null);
    }
  };

  const valorTotal = custos.reduce((acc, custo) => acc + Number(custo.cost), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#68686A]">Gerenciar Custos de Manutenção</DialogTitle>
          <DialogDescription className="text-[#68686A]">
            {produtoNome && `Produto: ${produtoNome}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Custos Adicionados */}
          {custos.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-[#68686A]">
                  Custos Adicionados ({custos.length})
                </Label>
                <span className="text-sm font-medium text-[#849D70]">
                  Total: R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="space-y-2">
                {custos.map((custo, index) => (
                  <Card key={custo.id || index} className="border border-gray-200">
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900">{custo.description}</span>
                            <span className="text-sm font-medium text-[#C2452D]">
                              R$ {Number(custo.cost).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removerCusto(custo.id, index)}
                          disabled={isDeleting === custo.id}
                          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          {isDeleting === custo.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Adicionar Novo Custo */}
          <div className="space-y-4 border-t pt-4">
            <Label className="text-sm font-medium text-[#68686A]">
              Adicionar Novo Custo
            </Label>

            <div>
              <Label htmlFor="descricao" className="text-sm font-medium text-[#68686A]">
                Descrição da Manutenção
              </Label>
              <Input
                id="descricao"
                value={novoCusto.description}
                onChange={(e) => setNovoCusto({ ...novoCusto, description: e.target.value })}
                placeholder="Ex: Lavanderia, Restauração, etc..."
                className="border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
              />
            </div>

            <div>
              <Label htmlFor="valor" className="text-sm font-medium text-[#68686A]">
                Valor do Custo
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#68686A]">R$</span>
                <Input
                  id="valor"
                  type="number"
                  step="0.01"
                  min="0"
                  value={novoCusto.cost || ""}
                  onChange={(e) => setNovoCusto({ ...novoCusto, cost: parseFloat(e.target.value) || 0 })}
                  placeholder="0,00"
                  className="pl-12 border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
                />
              </div>
            </div>

            <Button
              type="button"
              onClick={adicionarCusto}
              disabled={!novoCusto.description || novoCusto.cost <= 0 || isSubmitting}
              className="w-full bg-[#849D70] hover:bg-[#849D70]/90 text-white"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Adicionar Custo
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            className="bg-[#D4C070] hover:bg-[#849D70] text-white"
          >
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
